import crypto from 'crypto-js';
import { requestAddress, requestAttachmentList, requestDeleteAttachment } from 'store/api/application.api';
import type { Attachment, AttachPayload, Field, FieldValue, FilePayload, FormProperties, FormSection } from 'types/application';

const nricChecksum = (p: number) => {
    switch (p) {
        case 1:
            return 'A';
        case 2:
            return 'B';
        case 3:
            return 'C';
        case 4:
            return 'D';
        case 5:
            return 'E';
        case 6:
            return 'F';
        case 7:
            return 'G';
        case 8:
            return 'H';
        case 9:
            return 'I';
        case 10:
            return 'Z';
        case 11:
            return 'J';
        default:
            return '';
    }
};
const finChecksumFG = (p: number) => {
    switch (p) {
        case 1:
            return 'K';
        case 2:
            return 'L';
        case 3:
            return 'M';
        case 4:
            return 'N';
        case 5:
            return 'P';
        case 6:
            return 'Q';
        case 7:
            return 'R';
        case 8:
            return 'T';
        case 9:
            return 'U';
        case 10:
            return 'W';
        case 11:
            return 'X';
        default:
            return '';
    }
};
const finChecksumM = (p: number) => {
    switch (p) {
        case 1:
            return 'K';
        case 2:
            return 'L';
        case 3:
            return 'J';
        case 4:
            return 'N';
        case 5:
            return 'P';
        case 6:
            return 'Q';
        case 7:
            return 'R';
        case 8:
            return 'T';
        case 9:
            return 'U';
        case 10:
            return 'W';
        case 11:
            return 'X';
        default:
            return '';
    }
};

export const idNoValidation = (currentValue?: string, baseValue?: string) => {
    if (!baseValue || !currentValue) return true;
    if (currentValue.length < 9) return 'Invalid ID No.';

    const valueSplit = (currentValue as string).split('');
    const n1 = Number(valueSplit[1]) * 2;
    const n2 = Number(valueSplit[2]) * 7;
    const n3 = Number(valueSplit[3]) * 6;
    const n4 = Number(valueSplit[4]) * 5;
    const n5 = Number(valueSplit[5]) * 4;
    const n6 = Number(valueSplit[6]) * 3;
    const n7 = Number(valueSplit[7]) * 2;

    let nSum = n1 + n2 + n3 + n4 + n5 + n6 + n7;
    if (valueSplit[0] === 'G' || valueSplit[0] === 'T') nSum += 4;
    if (valueSplit[0] === 'M') nSum += 3;

    const reminder = nSum % 11;
    if (baseValue && ['NRC', 'NRP'].includes(baseValue)) {
        const p = 11 - reminder;
        const pValue = nricChecksum(p);
        const pattern = new RegExp(`^[ST]{1}[0-9]{7}${pValue}$`);
        return pattern.test(currentValue as string) ? true : 'Invalid ID No.';
    }

    // FIN
    const p = 11 - reminder;
    const pValue = valueSplit[0] === 'M' ? finChecksumM(p) : finChecksumFG(p);
    const pattern = new RegExp(`^[FGM]{1}[0-9]{7}${pValue}$`);
    return pattern.test(currentValue as string) ? true : 'Invalid ID No.';
};

export const formatDateTime = (date?: string, type?: 'date' | 'time' | 'monthShort' | 'withoutYear') => {
    if (!date) return '';
    if (date === '-') return date;

    const d = new Date(date);
    // const hour = `0${created.getHours()}`.slice(-2);
    // const min = `0${created.getMinutes()}`.slice(-2);
    const time = d.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
    if (type === 'time') return time;

    const year = d.getFullYear();
    const month = `0${d.getMonth() + 1}`.slice(-2);
    const day = `0${d.getDate()}`.slice(-2);
    if (type === 'date') return `${day}/${month}/${year}`;

    const customMonth = d.toLocaleString('default', { month: 'short' });
    if (type === 'withoutYear') return `${day} ${customMonth}`;
    if (type === 'monthShort') return `${day} ${customMonth} ${year}`;

    return `${day}/${month}/${year} ${time}`;
};

export const formatSavePlatformsToBeUsed = (platforms: string[]) => {
    if (!Array.isArray(platforms)) return {};

    const mainValue = ['Zoom', 'Facebook', 'Youtube', 'SISITICLive', 'Others'];
    const difference = mainValue
        .filter((element: string) => !platforms.includes(element))
        .reduce((accumulator: Record<string, string>, curr: string) => {
            return { ...accumulator, [curr]: 'false' };
        }, {});
    const checked = platforms.reduce(
        (accumulator: Record<string, string>, curr: string) => {
            return { ...accumulator, [curr]: 'true' };
        },
        { ...difference }
    );

    return checked;
};

export const retrievePlatforms = (platforms: Record<string, string>) => {
    if (!Object.values(platforms).length) return [];
    return Object.entries(platforms).reduce((accumulator: string[], current: string[]) => {
        const [key, value] = current;
        if (value === 'true') {
            accumulator = [...accumulator, key];
        }
        return accumulator;
    }, []);
};

export const handleRequiredChecker = (
    values: { [key: string]: FieldValue },
    info?: FormProperties,
    isInitialLoad = false,
    isSingpass?: boolean
) => {
    if (!info?.fields) return true;

    const isRequiredIncomplete = info.fields.some((section: FormSection) => {
        const getValue = (key: string) => {
            if (key in values) {
                return values[key];
            }

            const prefix = `${section.sectionID}-`;
            if (key.startsWith(prefix)) {
                const fallbackKey = key.substring(prefix.length);
                if (fallbackKey in values) {
                    return values[fallbackKey];
                }
            }

            return undefined;
        };

        if (section.rule) {
            const isSectHidden =
                section.rule.hidden &&
                (typeof section.rule.hidden === 'boolean' ? section.rule.hidden : section.rule.hidden({ baseValues: values, isSingpass }));
            if (isSectHidden) return false;
        }

        return section.fieldList.some((field: Field) => {
            const hidden =
                field.rule?.hidden &&
                (typeof field.rule.hidden === 'boolean' ? field.rule.hidden : field.rule.hidden({ baseValues: values }));
            const disabled =
                field.rule?.disabled &&
                (typeof field.rule.disabled === 'boolean' ? field.rule.disabled : field.rule.disabled({ baseValues: values }));
            const required =
                !disabled &&
                !hidden &&
                field.rule?.required &&
                (typeof field.rule.required === 'boolean' ? field.rule.required : field.rule.required({ baseValues: values }));

            if (required) {
                if (field.type === 'file') {
                    const fileData = getValue(`${section.sectionID}-${field.id}`) as { [key: string]: FieldValue }[] | undefined;
                    const validValues = Array.isArray(fileData)
                        ? fileData.filter((item: any) => !item.deleted && !item.removed && !item.error)
                        : [];

                    return !validValues?.length;
                }

                if (field.type === 'helperList') {
                    // For helperList, check the mainField (usually a file upload)
                    if (field.mainField) {
                        // HelperList fields use indexed format: sectionID-fieldID-index-mainFieldID
                        const mainFieldKey = `${section.sectionID}-${field.id}-0-${field.mainField.id}`;

                        if (field.mainField.type === 'file') {
                            const helperFileData = getValue(mainFieldKey) as { [key: string]: FieldValue }[] | undefined;
                            const validValues = Array.isArray(helperFileData)
                                ? helperFileData.filter((item: any) => !item.deleted && !item.removed && !item.error)
                                : [];
                            return !validValues?.length;
                        }

                        // For other mainField types, check if value is empty (treat 0, false as valid)
                        const mainValue = getValue(mainFieldKey);
                        if (mainValue === undefined || mainValue === null || mainValue === '') return true;
                        return false;
                    }

                    // Fallback to checking the main field id
                    const value = getValue(`${section.sectionID}-${field.id}`);
                    return value === undefined || value === null || value === '';
                }

                if (field.type === 'combo') {
                    // Check if either preField or mainField is empty (treat 0, false as valid)
                    const preValue = getValue(`${section.sectionID}-${field.id}-${field?.preField?.id || 0}`);
                    const mainValue = getValue(`${section.sectionID}-${field.id}-${field?.mainField?.id || 1}`);
                    const preEmpty = preValue === undefined || preValue === null || preValue === '';
                    const mainEmpty = mainValue === undefined || mainValue === null || mainValue === '';
                    return preEmpty || mainEmpty;
                }

                if (field.type === 'dateRadio') {
                    // Check if either preField or mainField is empty (treat 0, false as valid)
                    const preValue = getValue(`${section.sectionID}-${field.id}-${field?.preField?.id || 0}`);
                    const mainValue = getValue(`${section.sectionID}-${field.id}-${field?.mainField?.id || 1}`);
                    const preEmpty = preValue === undefined || preValue === null || preValue === '';
                    const mainEmpty = mainValue === undefined || mainValue === null || mainValue === '';
                    return preEmpty || mainEmpty;
                }

                if (field.type === 'table') {
                    const tableData = getValue(`${section.sectionID}-${field.id}`) as { [key: string]: FieldValue }[] | undefined;
                    const validValues = Array.isArray(tableData) ? tableData.filter((item: any) => !item.deleted) : [];

                    // For initial load, if the table has defaultRows or minForm, be more lenient
                    if (isInitialLoad && ((field as any).defaultRows > 0 || (field as any).minForm > 0)) {
                        // During initial load, don't mark tables with default rows as incomplete
                        // Let the FieldArray component initialize the rows first
                        return false;
                    }

                    // Check minLength requirement (e.g., auditors list requires at least 1 entry)
                    const minLength = (field.rule as any)?.minLength;
                    if (minLength !== undefined && validValues.length < minLength) {
                        return true; // Not enough rows
                    }

                    // Check minForm requirement
                    const minForm = (field as any).minForm;
                    if (minForm !== undefined && validValues.length < minForm) {
                        return true; // Not enough rows
                    }

                    const checkSubField =
                        !validValues?.length ||
                        validValues?.some((item: any) => {
                            return field.subFields?.some((subField: Field) => {
                                const reqSubField =
                                    subField.rule?.required &&
                                    (typeof subField.rule.required === 'boolean'
                                        ? subField.rule.required
                                        : subField.rule.required({ baseValues: values }));
                                // Check if subfield value is empty (treat 0, false as valid)
                                const subValue = item[subField.id];
                                const isEmpty = subValue === undefined || subValue === null || subValue === '';
                                return isEmpty && reqSubField;
                            });
                        });

                    return checkSubField;
                }

                if (field.type === 'shipDataSet') {
                    // For shipDataSet (DataSetEditor), check if array has valid entries
                    const shipDataRaw = getValue(`${section.sectionID}-${field.id}`);
                    const shipData = Array.isArray(shipDataRaw) ? shipDataRaw : [];
                    if (shipData.length === 0) {
                        return true; // Required but no data
                    }

                    // Check if at least one ship has meaningful data (not all empty fields)
                    const hasValidShip = shipData.some((ship: any) => {
                        if (!ship || typeof ship !== 'object') return false;
                        // Check if ship has at least one non-empty core field
                        const coreFields = ['shipName', 'callSign', 'licenceNo'];
                        return coreFields.some((field) => ship[field] && ship[field].toString().trim() !== '');
                    });

                    return !hasValidShip;
                }

                // Check if value is empty - treat 0, false as valid values, only check for truly empty
                const value = getValue(`${section.sectionID}-${field.id}`);
                return value === undefined || value === null || value === '';
            }
            return false;
        });
    });
    return isRequiredIncomplete;
};

export const attchmentSetting = (file: File, mimeTypes?: string[]) => {
    const fileName = file.name.split('.')[0];

    const attachment: Attachment = {
        ID: '',
        category: '',
        fileName: file.name,
        mimeType: file.type,
        file,
        randomValue: crypto.lib.WordArray.random(8).toString()
    };
    if (!mimeTypes?.includes(file.type)) attachment.errorType = 'fileType';
    if (file.size > 10485760) attachment.errorType = 'fileSize';
    if (file.size === 0) attachment.errorType = 'fileSizeEmpty';
    if (/^[/!@#$%^&*-]*$/.test(fileName)) attachment.errorType = 'fileNameCharacters';
    if (fileName.length > 255) attachment.errorType = 'fileNameLength';

    attachment.uploading = !attachment.errorType;
    attachment.error = !!attachment.errorType;
    return attachment;
};

export const attachmentActions = async (attachments: Attachment[], target: string, caseTypeID: string) => {
    const toUpload = attachments.filter((attachment: Attachment) => !attachment.ID && !attachment.error && !attachment.deleted);
    const toDelete = attachments.filter((attachment: Attachment) => attachment.saved && attachment.deleted);
    const uploaded = attachments.filter((attachment: Attachment) => attachment.saved);
    const pageInstructions = await Promise.all(
        toDelete.map(async (attachment: Attachment, index: number) => {
            await requestDeleteAttachment(attachment.ID, caseTypeID);
            const uploadedIdx = uploaded.findIndex((attachmentUploaded: Attachment) => attachmentUploaded.ID === attachment.ID);
            return {
                instruction: 'DELETE',
                target,
                listIndex: 1 + uploadedIdx - index
            };
        })
    );

    return { toUpload, pageInstructions };
};

// uploadedFiles: AttachPayload[] removed
export const attachmentPayloadSetting = (attachments: Attachment[]) => {
    const payload = attachments.map((attachment: Attachment) => {
        // const nameWithRandom = `${attachment.fileName.split('.')[0]}-${attachment.randomValue}`;
        // const uploadedData = uploadedFiles.find(
        //     (attachmentUploaded: AttachPayload) => attachmentUploaded.name === nameWithRandom || attachmentUploaded.ID === attachment.ID
        // );
        return {
            FileName: attachment.fileName,
            FileType: attachment.mimeType,
            FileID: attachment.ID
        };
    });
    return payload;
};

export const attachmentRetrieve = (data?: FilePayload[]) => {
    const value = data?.map(({ FileID, FileName, FileType }: FilePayload) => {
        const attachment = {
            ID: FileID,
            category: '',
            fileName: FileName,
            mimeType: FileType,
            saved: true
        };
        return attachment;
    });

    return value;
};

export const attachmentPayloadReverse = (data?: AttachPayload[]) => {
    const value = data?.map(({ ID, fileName, mimeType, category, error, errorType, randomValue }: AttachPayload) => {
        const attachment = {
            ID,
            category,
            fileName,
            mimeType,
            error,
            errorType,
            randomValue
        };
        return attachment as Attachment;
    });

    return value || [];
};

export const getFileID = (trackedIDs: string[], uploadedFiles: AttachPayload[], attachments: Attachment[]) => {
    const attachmentTemp = attachments.map((attachment: Attachment) => {
        const uploadedData = uploadedFiles.find(
            (attachmentUploaded: AttachPayload) =>
                attachmentUploaded.fileName === attachment.fileName && !trackedIDs.includes(attachmentUploaded.ID)
        );

        if (uploadedData?.ID) {
            const data = {
                ...attachment,
                ID: uploadedData.ID
            };
            return data;
        }

        const notFound = {
            ...attachment,
            error: true,
            errorType: attachment.errorType || 'not-found'
        };
        return notFound;
    });

    return attachmentTemp;
};

export const tableActions = (list: { [key: string]: FieldValue }[], target: string) => {
    const uploaded = list
        .filter((item: { [key: string]: FieldValue }) => item.uploaded === 'true')
        .map((item: { [key: string]: FieldValue }, index: number) => {
            item.index = index;
            return item;
        });
    const toDelete = uploaded.filter((item: { [key: string]: FieldValue }) => item.deleted);
    const pageInstructions = toDelete.map((item: { [key: string]: FieldValue }, index: number) => {
        return {
            instruction: 'DELETE',
            target,
            listIndex: 1 + Number(item.index) - index
        };
    });

    return pageInstructions;
};

export const payloadDeleteApplication = {
    content: {
        pyGotoStage: 'ALT1',
        WithdrawalRemarks: 'Draft Deleted',
        WithdrawalDate: new Date()
    }
};

export const payloadWithdrawApplication = (withdrawalReason: string, stage: 'ALT1' | 'ALT2' = 'ALT1') => ({
    content: {
        pyGotoStage: stage,
        WithdrawalRemarks: withdrawalReason,
        WithdrawalDate: new Date().toISOString()
    }
});

export const payloadFVGDeleteApplication = {
    caseTypeID: 'IMDA-FVGCS-Work-FVG-VideoGames',
    content: {
        pyGotoStage: 'Delete Draft'
    }
};

export const formDeleteApplication = [
    {
        id: 'application-delete',
        formID: 'application-delete',
        hasError: true,
        values: {}
    }
];

export const formWithdrawApplication = [
    {
        id: 'application-withdraw',
        formID: 'application-withdraw',
        hasError: true,
        values: {}
    }
];

export const requestLicenceAttachment = async (fileName: string, caseTypeID: string) => {
    const response = await requestAttachmentList(caseTypeID);
    const attach = response.find((attachment: any) => {
        return attachment.fileName === fileName;
    });
    return attach || '';
};

export const validateStreetName = async (postalCode: string, currentValue: string) => {
    const response = await requestAddress(postalCode);
    const address = response.find((address: any) => {
        return address.StreetName.toLowerCase() !== currentValue.toLowerCase();
    });
    return address;
};

export const isEnglish = (input: string) => {
    const englishPattern = /^(?:[A-Za-z0-9\s!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]*)$/;
    return englishPattern.test(input);
};
